import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  ArrowLeft,
  Train,
  Sparkles,
  Send,
  Phone,
  Mail,
  BedDouble,
  Users,
  Bath,
  Lock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImageCarousel from "@/components/ImageCarousel";
import AmenitiesGrid from "@/components/AmenitiesGrid";
import { getApartmentById, ADDRESS_DISCLOSURE_NOTE } from "@/data/apartments";
import { useInquiry } from "@/contexts/InquiryContext";

export default function ApartmentDetail() {
  const { id } = useParams();
  const apartment = getApartmentById(id);
  const { openInquiry } = useInquiry();

  useEffect(() => {
    if (apartment) {
      document.title = `${apartment.short_name} — Kenneth Nathanael Lukman LLC`;
    }
  }, [apartment]);

  if (!apartment) return <Navigate to="/" replace />;

  const mapsQuery = encodeURIComponent(`${apartment.area}, London`);
  // Map uses an approximate location centred within the neighbourhood — not the exact building.
  const mapUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${apartment.coordinates.lat},${apartment.coordinates.lng}&zoom=14&size=900x420&markers=${apartment.coordinates.lat},${apartment.coordinates.lng},red-pushpin`;

  return (
    <div
      data-testid={`page-apartment-${apartment.id}`}
      className="min-h-screen bg-[#F7F3EA]"
    >
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-12 md:pt-36 md:pb-16 bg-[#F7F3EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link
            to="/#apartments"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-sm text-[#5C6B4D] hover:text-[#6E7F5B] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to the collection
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-6xl"
          >
            <div className="max-w-3xl">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                {apartment.area} · London
              </div>
              <h1
                data-testid="detail-headline"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2B3324] leading-[1.05]"
              >
                {apartment.name}
              </h1>
              <div className="flex items-center gap-2 mt-5 text-[#5C6B4D]">
                <MapPin className="w-4 h-4 text-[#C5A059]" />
                <span className="text-base">{apartment.area}, London</span>
              </div>
              <div
                data-testid="address-disclosure"
                className="mt-3 inline-flex items-start gap-2 text-xs text-[#5C6B4D] bg-[#F7F3EA] border border-[#E5E0D5] rounded-full px-3.5 py-1.5"
              >
                <Lock className="w-3 h-3 text-[#C5A059] mt-0.5 shrink-0" />
                <span>{ADDRESS_DISCLOSURE_NOTE}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => openInquiry(apartment.id)}
              data-testid="detail-inquire-cta-top"
              className="hidden md:inline-flex items-center justify-center gap-2 bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white px-7 py-3.5 rounded-lg font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-sm shrink-0"
            >
              <Send className="w-4 h-4" />
              Discuss a Similar Mandate
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            <ImageCarousel images={apartment.images} alt={apartment.name} />
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left content */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-14">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                Case Study
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#2B3324]">
                The advisory outcome.
              </h2>
              <div className="gold-accent mt-4 mb-6" />
              <p className="text-[#5C6B4D] text-base md:text-lg leading-relaxed font-light whitespace-pre-line">
                {apartment.description}
              </p>
            </div>

            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                Asset Profile
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#2B3324]">
                The property at a glance.
              </h2>
              <div className="gold-accent mt-4 mb-8" />
              <AmenitiesGrid features={apartment.features} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-7 rounded-xl border border-[#E5E0D5]/70">
                <Sparkles className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="font-display text-lg font-medium tracking-tight text-[#2B3324] mt-4">
                  Nearby
                </h3>
                <ul
                  data-testid="nearby-list"
                  className="mt-3 space-y-2 text-sm text-[#5C6B4D]"
                >
                  {apartment.nearby_attractions.map((n) => (
                    <li key={n} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C5A059] mt-2 shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white p-7 rounded-xl border border-[#E5E0D5]/70">
                <Train className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="font-display text-lg font-medium tracking-tight text-[#2B3324] mt-4">
                  Transport
                </h3>
                <p
                  data-testid="transport-info"
                  className="mt-3 text-sm text-[#5C6B4D] leading-relaxed"
                >
                  {apartment.transport}
                </p>
              </div>
            </div>

            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                Location
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#2B3324]">
                Find us in {apartment.area}.
              </h2>
              <div className="gold-accent mt-4 mb-6" />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-link"
                className="block relative aspect-[21/9] w-full rounded-xl overflow-hidden border border-[#E5E0D5] group"
              >
                <img
                  src={mapUrl}
                  alt={`Approximate map of the ${apartment.area} neighbourhood`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.classList.add(
                      "bg-gradient-to-br",
                      "from-[#E5E0D5]",
                      "to-[#F7F3EA]"
                    );
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase opacity-80">
                      Approximate Location
                    </div>
                    <div className="font-display text-lg md:text-xl mt-1">
                      {apartment.area}, London
                    </div>
                    <div className="text-xs opacity-80 mt-1">
                      {ADDRESS_DISCLOSURE_NOTE}
                    </div>
                  </div>
                  <div className="bg-white/95 text-[#2B3324] rounded-full px-4 py-2 text-xs font-medium">
                    Open in Maps →
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right sticky reserve card */}
          <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-28">
            <div
              data-testid="reserve-card"
              className="bg-white p-7 md:p-8 rounded-2xl border border-[#E5E0D5] shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                Discuss a Mandate
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#2B3324] leading-snug">
                A similar advisory for{" "}
                <span className="italic text-[#6E7F5B]">
                  your property
                </span>
              </h3>
              <p className="mt-3 text-sm text-[#5C6B4D] leading-relaxed">
                If you own a comparable asset in prime London and would like a
                written blueprint, refurbishment oversight, or operator search,
                our team responds to every enquiry within one business day.
              </p>

              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#F7F3EA] rounded-lg py-3">
                  <BedDouble className="w-4 h-4 text-[#C5A059] mx-auto" />
                  <div className="text-xs text-[#5C6B4D] mt-1.5">Bedrooms</div>
                  <div className="text-sm font-medium text-[#2B3324]">
                    {apartment.features.bedrooms}
                  </div>
                </div>
                <div className="bg-[#F7F3EA] rounded-lg py-3">
                  <Bath className="w-4 h-4 text-[#C5A059] mx-auto" />
                  <div className="text-xs text-[#5C6B4D] mt-1.5">Bathrooms</div>
                  <div className="text-sm font-medium text-[#2B3324]">
                    {apartment.features.bathrooms}
                  </div>
                </div>
                <div className="bg-[#F7F3EA] rounded-lg py-3">
                  <Users className="w-4 h-4 text-[#C5A059] mx-auto" />
                  <div className="text-xs text-[#5C6B4D] mt-1.5">Capacity</div>
                  <div className="text-sm font-medium text-[#2B3324]">
                    {apartment.features.guests}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => openInquiry(apartment.id)}
                data-testid="detail-inquire-cta"
                className="mt-7 w-full bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white py-4 rounded-lg font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-sm flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Discuss a Similar Mandate
              </button>

              <div className="mt-6 pt-6 border-t border-[#E5E0D5] space-y-3 text-sm">
                <a
                  href="tel:+17622269357"
                  className="flex items-center gap-3 text-[#5C6B4D] hover:text-[#6E7F5B] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#C5A059]" />
                  +1 (762) 226-9357
                </a>
                <a
                  href="mailto:info@kenneth-llc.com"
                  className="flex items-center gap-3 text-[#5C6B4D] hover:text-[#6E7F5B] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#C5A059]" />
                  info@kenneth-llc.com
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E0D5] p-4">
        <button
          type="button"
          onClick={() => openInquiry(apartment.id)}
          data-testid="detail-inquire-cta-mobile"
          className="w-full bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white py-3.5 rounded-lg font-medium tracking-wide flex items-center justify-center gap-2 shadow-sm"
        >
          <Send className="w-4 h-4" />
          Discuss a Mandate
        </button>
      </div>

      <Footer />
    </div>
  );
}
